import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import { Star, ArrowRight, Palmtree, Ship, Sparkles, Award, Users, Shield, Calendar as CalendarIcon, Trophy } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import InstaFeed from "@/components/InstaFeed";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1599325601183-042bed55081c";

// Animated Counter Component
const AnimatedCounter = ({ value, suffix = "", duration = 2 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const count = useMotionValue(0);
  const rounded = useTransform(count, (latest) => Math.floor(latest).toLocaleString('en-IN'));
  const [displayValue, setDisplayValue] = useState("0");

  useEffect(() => {
    if (inView) {
      const controls = animate(count, value, { duration });
      const unsubscribe = rounded.on("change", (v) => setDisplayValue(v));
      return () => {
        controls.stop();
        unsubscribe();
      };
    }
  }, [inView, value, duration, count, rounded]);

  return (
    <span ref={ref} className="tabular-nums">
      {displayValue}{suffix}
    </span>
  );
};

const Home = () => {
  const [featuredPackages, setFeaturedPackages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [packagesRes, testimonialsRes] = await Promise.all([
        axios.get(`${API}/packages?featured=true`),
        axios.get(`${API}/testimonials?featured=true`),
      ]);
      setFeaturedPackages(packagesRes.data.slice(0, 3));
      setTestimonials(testimonialsRes.data.slice(0, 3));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="home-page" className="grain-texture">
      <SEO
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "TravelAgency",
          name: "Andaman Avalon",
          url: "https://www.andamanavalon.com",
          logo: "https://www.andamanavalon.com/logo512.png",
          description:
            "Luxury Andaman Islands tour operator offering curated honeymoon, family, adventure and budget packages with online booking.",
          areaServed: { "@type": "Place", name: "Andaman & Nicobar Islands, India" },
          priceRange: "₹₹",
          sameAs: [],
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.9",
            reviewCount: "150",
          },
        }}
      />
      <Navbar />

      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative h-screen flex items-center justify-center overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Andaman Beach"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0"></div>
        </div>

        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="accent-label text-white mb-4 block" data-testid="hero-label">
              LUXURY ISLE TRAVEL
            </span>
            <h1
              data-testid="hero-title"
              className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold text-white mb-6 leading-tight"
            >
              Discover Paradise in the Andaman Islands
            </h1>
            <p
              data-testid="hero-subtitle"
              className="text-base sm:text-lg text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed"
            >
              Embark on extraordinary journeys through pristine beaches, crystal-clear waters, and lush tropical landscapes. Your dream island escape awaits.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/packages" data-testid="hero-explore-btn" className="btn-primary">
                Explore Packages
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-btn"
                className="btn-secondary"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="accent-label">WHY CHOOSE US</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif mt-4 mb-4">
              Experience the Andaman Difference
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Palmtree,
                title: "Curated Experiences",
                description: "Handpicked destinations and activities for unforgettable memories.",
                testid: "feature-curated"
              },
              {
                icon: Ship,
                title: "Expert Guidance",
                description: "Local insights and seamless travel planning from our experienced team.",
                testid: "feature-expert"
              },
              {
                icon: Sparkles,
                title: "Luxury & Comfort",
                description: "Premium accommodations and personalized service throughout your journey.",
                testid: "feature-luxury"
              },
            ].map((feature, index) => (
              <motion.div
                key={index}
                data-testid={feature.testid}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="admin-card text-center hover:shadow-lg transition-shadow duration-300"
              >
                <feature.icon className="w-12 h-12 mx-auto mb-4 text-secondary" />
                <h3 className="text-xl font-serif mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges - Why Choose Andaman Avalon */}
      <section className="py-24 bg-primary text-primary-foreground relative overflow-hidden" data-testid="trust-badges-section">
        {/* Decorative background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: "radial-gradient(circle at 20% 50%, rgba(200, 169, 126, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(200, 169, 126, 0.2) 0%, transparent 50%)"
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="accent-label text-secondary">WHY CHOOSE ANDAMAN AVALON</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif mt-4 mb-4 text-white">
              Trusted by Thousands of Travellers
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              Our commitment to excellence has made us the preferred choice for Andaman tourism
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: CalendarIcon, value: 12, suffix: "+", label: "Years in Business", desc: "Since 2013", testid: "stat-years" },
              { icon: Users, value: 15000, suffix: "+", label: "Happy Travellers", desc: "Across 40+ Countries", testid: "stat-travellers" },
              { icon: Shield, value: 100, suffix: "%", label: "Safety Certified", desc: "Trusted Partners", testid: "stat-safety" },
              { icon: Trophy, value: 25, suffix: "+", label: "Industry Awards", desc: "Tourism Excellence", testid: "stat-awards" },
            ].map((stat, index) => (
              <motion.div
                key={index}
                data-testid={stat.testid}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15, duration: 0.6 }}
                className="text-center"
              >
                <div className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-secondary/20 backdrop-blur-sm mb-4 border border-secondary/30">
                  <stat.icon className="w-8 h-8 text-secondary" />
                </div>
                <div className="text-4xl sm:text-5xl font-serif font-semibold text-secondary mb-2">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-lg font-medium text-white mb-1">{stat.label}</div>
                <div className="text-sm text-white/60">{stat.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Packages */}
      <section className="py-24 bg-muted" data-testid="featured-packages">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="accent-label">FEATURED PACKAGES</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif mt-4 mb-4">
              Our Most Popular Tours
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredPackages.map((pkg, index) => (
                <motion.div
                  key={pkg.id}
                  data-testid={`featured-package-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="package-card bg-white rounded-lg overflow-hidden border border-border/50 shadow-sm"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={pkg.image_url}
                      alt={pkg.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4 bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-xs font-semibold uppercase">
                      {pkg.category}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-serif mb-2">{pkg.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{pkg.duration}</p>
                    <p className="text-muted-foreground mb-4 line-clamp-2">
                      {pkg.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold text-primary">
                        ₹{pkg.price.toLocaleString('en-IN')}
                      </span>
                      <Link
                        to={`/packages/${pkg.id}`}
                        data-testid={`view-package-${index}`}
                        className="text-secondary hover:text-secondary/80 font-medium flex items-center"
                      >
                        View Details <ArrowRight className="ml-1" size={16} />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/packages" data-testid="view-all-packages-btn" className="btn-primary">
              View All Packages
            </Link>
          </div>
        </div>
      </section>

      {/* Customer Photo Gallery / Travel Reels — replaced by InstaFeed */}
      <InstaFeed handle="andamanavalon" profileUrl="https://www.instagram.com/andamanavalon" />
      <section className="hidden" data-testid="gallery-section-old">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className="text-center">
            <span className="accent-label">TRAVEL MOMENTS</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif mt-4 mb-4">
              Real Moments, Real Memories
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Glimpses from our travellers' unforgettable Andaman adventures
            </p>
          </div>
        </div>

        {/* Masonry Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {[
              { src: "https://images.unsplash.com/photo-1586359716568-3e1907e4cf9f", height: "h-64 md:h-80", tag: "Radhanagar Beach" },
              { src: "https://images.unsplash.com/photo-1544551763-46a013bb70d5", height: "h-48 md:h-56", tag: "Scuba Diving" },
              { src: "https://images.unsplash.com/photo-1575388104683-e076ee9ccaa0", height: "h-64 md:h-72", tag: "Sunset Walks" },
              { src: "https://images.unsplash.com/photo-1561501900-3701fa6a0864", height: "h-48 md:h-56", tag: "Luxury Stays" },
              { src: "https://images.unsplash.com/photo-1708649290066-5f617003b93f", height: "h-56 md:h-64", tag: "Coral Reefs" },
              { src: "https://images.unsplash.com/photo-1599325601183-042bed55081c", height: "h-64 md:h-72", tag: "White Sands" },
              { src: "https://images.unsplash.com/photo-1586500036065-bdaeac7a4feb", height: "h-52 md:h-60", tag: "Palm Paradise" },
              { src: "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9", height: "h-64 md:h-80", tag: "Island Life" },
            ].map((photo, index) => (
              <motion.div
                key={index}
                data-testid={`gallery-item-${index}`}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: (index % 4) * 0.1, duration: 0.5 }}
                whileHover={{ y: -4 }}
                className={`relative ${photo.height} rounded-xl overflow-hidden group cursor-pointer`}
              >
                <img
                  src={photo.src}
                  alt={photo.tag}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                {/* Tag */}
                <div className="absolute bottom-3 left-3 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                  <span className="inline-block bg-secondary/90 backdrop-blur-sm text-secondary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                    #{photo.tag.replace(/\s+/g, '')}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Instagram-style CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <p className="text-muted-foreground mb-4">
              Share your Andaman story with us
            </p>
            <div className="inline-flex items-center gap-2 text-secondary font-semibold">
              <span className="text-lg">#AndamanAvalonMoments</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-muted" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="accent-label">TESTIMONIALS</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif mt-4 mb-4">
              What Our Travelers Say
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  data-testid={`testimonial-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="admin-card"
                >
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-secondary text-secondary" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-4 italic">
                    "{testimonial.review}"
                  </p>
                  <div>
                    <p className="font-semibold text-primary">{testimonial.customer_name}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.package_name}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary text-primary-foreground" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif mb-6 text-white">
            Ready to Start Your Adventure?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Let us help you create memories that will last a lifetime. Contact us today for personalized package recommendations.
          </p>
          <Link to="/contact" data-testid="cta-contact-btn" className="btn-secondary">
            Get in Touch
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
